import React, { useState, useEffect } from 'react';
import { 
  User, 
  Phone, 
  MapPin, 
  Send, 
  X, 
  Check, 
  CreditCard,
  Truck
} from 'lucide-react';

interface Product {
  id: string;
  name: string;
  arabicName: string;
  image: string;
  images: string[];
  price: number;
  originalPrice?: number;
  description: string;
  features: string[];
  badge?: string;
  rating: number;
  reviews: number;
  isPopular: boolean;
}

interface OrderFormData {
  name: string;
  phone: string;
  address: string;
  city: string;
  selectedProduct: Product | null;
}

interface OrderFormProps {
  preselectedProductId?: string;
}

declare global {
  interface Window {
    fbq: any;
  }
}

const PointingFinger = () => (
  <div className="flex justify-center mt-2 mb-4">
    <div className="relative">
      <div 
        className="text-5xl md:text-6xl"
        style={{
          animation: 'bounceUpDown 1.5s ease-in-out infinite'
        }}
      >
        👇
      </div>
      <style jsx>{`
        @keyframes bounceUpDown {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
      `}</style>
    </div>
  </div>
);

const OrderForm: React.FC<OrderFormProps> = ({ preselectedProductId }) => {
  const products: Product[] = [
        {
      id: 'cadenas',
      name: 'Cadenas',
      arabicName: 'قفل',
      image: 'https://i.ibb.co/BKHtH0M9/Whats-App-Image-2025-10-14-at-21-40-18.jpg',
      images: [
        'https://i.ibb.co/BKHtH0M9/Whats-App-Image-2025-10-14-at-21-40-18.jpg',
      ],
      price: 23.000,
      originalPrice: 59.900,
      description: 'قفل معدني متين للحماية الإضافية من السرقة. مثالي للاستخدام اليومي.',
      features: ['قوي ومتين', 'مقاوم للصدأ', 'تصميم صغير وسهل الاستخدام'],
      badge: 'الأكثر طلباً',
      rating: 4.7,
      reviews: 529,
      isPopular: true
    },
        {
      id: 'grand-support',
      name: 'Grand Support',
      arabicName: 'سيبور كبير',
      image: 'https://i.ibb.co/LDSjNXWr/Whats-App-Image-2025-11-15-at-18-17-26.jpg',
      images: [
        'https://i.ibb.co/RpLHcvhn/Whats-App-Image-2025-11-09-at-11-21-31.jpg',
      ],
      price: 33.000,
      originalPrice: 39.900,
      description: 'داعم قوي ومتين لتثبيت البلوك أو الأقفال على السيارة بسهولة وأمان.',
      features: ['مادة عالية الجودة', 'مناسب لجميع الأنواع', 'تصميم عملي ومتين'],
      badge: 'عملي',
      rating: 4.8,
      reviews: 378,
      isPopular: false
    },
    {
      id: 'block-pedal',
      name: 'Block Pedal',
      arabicName: 'بلوك دواسة',
      image: 'https://i.ibb.co/4ZmHmKJd/Whats-App-Image-2025-09-10-at-3-04-51-PM.jpg',
      images: [
        'https://i.ibb.co/4ZmHmKJd/Whats-App-Image-2025-09-10-at-3-04-51-PM.jpg',
        'https://i.ibb.co/Zp396GD2/Whats-App-Image-2025-09-12-at-15-30-53.jpg'
      ],
      price: 49.900,
      originalPrice: 79.900,
      description: 'جهاز مكافحة السرقة الأكثر فعالية وموثوقية. يمنع تحريك السيارة بشكل كامل.',
      features: ['تركيب في دقيقتين', 'متوافق مع جميع السيارات', 'مقاوم للكسر', 'ضمان سنة كاملة'],
      badge: 'الأكثر مبيعاً',
      rating: 4.9,
      reviews: 1247,
      isPopular: true
    },
    {
      id: 'cadenas-alarm',
      name: 'Cadenas Alarm',
      arabicName: 'قفل إنذار',
      image: 'https://i.ibb.co/whNMSK8j/image-11.png',
      images: [
        'https://i.ibb.co/whNMSK8j/image-11.png',
      ],
      price: 30.000,
      originalPrice: 59.900,
      description: 'قفل ذكي مع نظام إنذار صوتي قوي. حماية إضافية لسيارتك.',
      features: ['إنذار صوتي 120 ديسيبل', 'مقاوم للماء', 'بطارية طويلة المدى', 'سهل الاستخدام'],
      badge: 'جديد',
      rating: 4.9,
      reviews: 1247,
      isPopular: true
    },
    {
      id: 'block-pedal-2x1',
      name: 'Block Pedal 2x1',
      arabicName: 'بلوك دواسة عرض خاص',
      image: 'https://i.ibb.co/bjWs7K8G/Whats-App-Image-2025-09-25-at-15-10-03.jpg',
      images: [
        'https://i.ibb.co/bjWs7K8G/Whats-App-Image-2025-09-25-at-15-10-03.jpg',
      ],
      price: 93.000,
      originalPrice: 150.000,
      description: 'جهاز مكافحة السرقة الأكثر فعالية وموثوقية. يمنع تحريك السيارة بشكل كامل.',
      features: ['تركيب في دقيقتين', 'متوافق مع جميع السيارات', 'مقاوم للكسر', 'ضمان سنة كاملة'],
      badge: 'الأكثر مبيعاً',
      rating: 4.9,
      reviews: 1247,
      isPopular: true
    },
    {
      id: 'bloc-pedal-alarme',
      name: 'Bloc Pedal Alarme',
      arabicName: 'بلوك دواسة مع إنذار',
      image: 'https://i.ibb.co/nNHLZHtR/Untitled-design-1.png',
      images: [
        'https://i.ibb.co/mC1dz3XC/Whats-App-Image-2025-09-25-at-15-10-16.jpg',
      ],
      price: 59.900,
      originalPrice: 99.900,
      description: 'الحل الأمثل: بلوك دواسة مع نظام إنذار مدمج. حماية مضاعفة لسيارتك.',
      features: ['حماية مضاعفة', 'إنذار مدمج', 'تقنية متطورة', 'سهل التركيب'],
      badge: 'متطور',
      rating: 4.9,
      reviews: 1247,
      isPopular: true
    },
    {
      id: 'bloc-volant',
      name: 'Bloc volant',
      arabicName: 'بلوك عجلة القيادة',
      image: 'https://i.ibb.co/QvYR0VJt/Whats-App-Image-2025-11-08-at-13-52-54.jpg',
      images: [
        'https://i.ibb.co/QvYR0VJt/Whats-App-Image-2025-11-08-at-13-52-54.jpg',
      ],
      price: 79.900,
      originalPrice: 99.900,
      description: 'مانع عجلة القيادة المطور. يمنع تماماً دوران عجلة القيادة مما يجعل السيارة غير قابلة للقيادة.',
      features: ['تركيب في 30 ثانية', 'قفل عجلة قيادة متين', 'مفتاح أمان فريد', 'مقاوم للصدأ', 'ضمان سنة كاملة'],
      badge: 'جديد',
      rating: 4.9,
      reviews: 1247,
      isPopular: true
    },
    {
      id: 'support',
      name: 'Support',
      arabicName: 'سيبور',
      image: 'https://i.ibb.co/NnHWvKxG/Whats-App-Image-2025-11-15-at-18-17-03.jpg',
      images: [
        'https://i.ibb.co/ZprHBCPL/Gemini-Generated-Image-z12niz12niz12niz.png',
      ],
      price: 33.000,
      originalPrice: 39.900,
      description: 'سبورت كبير الحجم يوفر ثباتًا إضافيًا للبلوك، مناسب للمركبات الكبيرة.',
      features: ['قوي ومتين', 'مناسب للمركبات الكبيرة', 'تصميم محسّن للاستقرار'],
      badge: 'مميز',
      rating: 4.8,
      reviews: 412,
      isPopular: false
    }
  ];

  const [formData, setFormData] = useState<OrderFormData>({
    name: '',
    phone: '',
    address: '',
    city: '',
    selectedProduct: null
  });
  
  const [errors, setErrors] = useState<Partial<OrderFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [hasTrackedViewContent, setHasTrackedViewContent] = useState(false);

  const tunisianCities = [
    'تونس', 'صفاقس', 'سوسة', 'القيروان', 'بنزرت', 'قابس', 'أريانة',
    'قفصة', 'المنستير', 'باجة', 'نابل', 'زغوان', 'المهدية', 'تطاوين',
    'قبلي', 'سليانة', 'الكاف', 'توزر', 'مدنين', 'بن عروس', 'منوبة',
    'جندوبة', 'القصرين', 'سيدي بوزيد'
  ];

  // Handle preselected product from URL parameter
  useEffect(() => {
    if (preselectedProductId) {
      const product = products.find(p => p.id === preselectedProductId);
      if (product) {
        setFormData(prev => ({
          ...prev,
          selectedProduct: product
        }));
        setShowModal(true);
        
        // Track Facebook event for product view
        if (typeof window.fbq !== 'undefined') {
          window.fbq('track', 'ViewContent', {
            content_ids: [product.id],
            content_name: product.arabicName,
            content_type: 'product',
            value: product.price,
            currency: 'TND'
          });
        }
      }
    }
  }, [preselectedProductId]);

  useEffect(() => {
    if (!hasTrackedViewContent && typeof window.fbq !== 'undefined') {
      window.fbq('track', 'ViewContent', {
        content_type: 'product',
        content_ids: products.map(p => p.id),
        content_name: 'Order Form Products',
        currency: 'TND'
      });
      setHasTrackedViewContent(true);
    }
  }, [hasTrackedViewContent, products]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name as keyof OrderFormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleBuyClick = (product: Product) => {
    setFormData(prev => ({
      ...prev,
      selectedProduct: product
    }));
    setShowModal(true);

    if (typeof window.fbq !== 'undefined') {
      window.fbq('track', 'AddToCart', {
        content_ids: [product.id],
        content_name: product.arabicName,
        content_type: 'product',
        value: product.price || 0,
        currency: 'TND',
        quantity: 1
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<OrderFormData> = {};
    
    if (!formData.name.trim()) newErrors.name = 'الاسم مطلوب';
    if (!formData.phone.trim()) newErrors.phone = 'رقم الهاتف مطلوب';
    if (!formData.address.trim()) newErrors.address = 'العنوان مطلوب';
    if (!formData.city.trim()) newErrors.city = 'المدينة مطلوبة';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !formData.selectedProduct) return;
    
    setIsSubmitting(true);
    
    try {
      const total = formData.selectedProduct.price;
      
      const whatsappMessage = `🛒 طلبية جديدة من Totale Sécurité

👤 العميل: ${formData.name}
📞 الهاتف: ${formData.phone}
🏙️ المدينة: ${formData.city}
📍 العنوان: ${formData.address}
🛍️ المنتج: ${formData.selectedProduct.arabicName}
💰 المجموع: ${total.toFixed(3)} د.ت

✅ دفع عند الاستلام
🚚 توصيل مجاني`;

      const phoneNumber = "21629199719";
      const apiKey = "2348703";
      
      const callMeBotUrl = `https://api.callmebot.com/whatsapp.php?phone=${phoneNumber}&text=${encodeURIComponent(whatsappMessage)}&apikey=${apiKey}`;
      
      await fetch(callMeBotUrl, { mode: 'no-cors' });
      
      if (typeof window.fbq !== 'undefined') {
        window.fbq('track', 'Purchase', {
          currency: 'TND',
          value: total,
          content_ids: [formData.selectedProduct.id],
          content_type: 'product',
          contents: [{
            id: formData.selectedProduct.id,
            quantity: 1,
            item_price: formData.selectedProduct.price || 0
          }],
          content_name: formData.selectedProduct.arabicName,
          num_items: 1
        });
        
        window.fbq('track', 'Lead', {
          currency: 'TND',
          value: total,
          content_name: 'Order Form Submission'
        });

        window.fbq('trackCustom', 'WhatsAppOrder', {
          currency: 'TND',
          value: total,
          order_type: 'whatsapp',
          products: formData.selectedProduct.arabicName
        });
      }
      
      setIsSubmitting(false);
      setShowModal(false);
      setShowSuccess(true);
      
      setFormData({
        name: '',
        phone: '',
        address: '',
        city: '',
        selectedProduct: null
      });
      
    } catch (error) {
      setIsSubmitting(false);
      setShowModal(false);
      setShowError(true);
      console.error('Order submission failed:', error);
    }
  };

  return (
    <section id="order" className="py-16 bg-gradient-to-br from-blue-50 via-white to-red-50 relative">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">
            <span className="gradient-text">اطلب الآن</span>
          </h2>
          <p className="text-xl text-gray-600">اختر منتجك وأكمل الطلب بسهولة</p>
        </div>

        {/* Video section with title */}
        <div className="max-w-6xl mx-auto mb-8">
          <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">منتجات عديمة الفأئدة وشهدات الحرفاء...</h3>
          <div className="flex justify-center">
            <iframe 
              src="https://www.facebook.com/plugins/video.php?height=476&href=https%3A%2F%2Fwww.facebook.com%2Freel%2F1522394962350685%2F&show_text=false&width=267&t=0&autoplay=true" 
              width="267" 
              height="476" 
              style={{border:"none",overflow:"hidden"}} 
              scrolling="no" 
              frameborder="0" 
              allowfullscreen="true" 
              allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" 
              allowFullScreen="true"
            ></iframe>
          </div>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden p-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-8 text-center">المنتجات المتوفرة</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((product, index) => (
                <React.Fragment key={product.id}>
                  <div 
                    className={`relative rounded-2xl overflow-hidden border-2 transition-all duration-300 hover:border-gray-300 hover-lift ${
                      preselectedProductId === product.id 
                        ? 'border-red-500 shadow-lg' 
                        : 'border-gray-200'
                    }`}
                  >
                    <div className="aspect-square overflow-hidden">
                      <img 
                        src={product.image} 
                        alt={product.arabicName} 
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                      />
                    </div>
                    
                    <div className="p-4 bg-white">
                      <h4 className="font-semibold text-gray-800 text-right mb-1">{product.arabicName}</h4>
                      <p className="text-sm text-gray-600 text-right mb-3">{product.name}</p>
                      
                      <div className="flex items-center justify-between">
                        <div className="text-right">
                          <span className="text-lg font-bold text-red-600">{product.price.toFixed(3)} د.ت</span>
                          {product.originalPrice && (
                            <span className="block text-sm text-gray-400 line-through">
                              {product.originalPrice.toFixed(3)} د.ت
                            </span>
                          )}
                        </div>
                        
                        {product.badge && (
                          <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-semibold">
                            {product.badge}
                          </span>
                        )}
                      </div>
                      
                      <button 
                        onClick={() => handleBuyClick(product)}
                        className="w-full mt-4 btn-primary py-3 text-lg"
                      >
                        Acheter
                      </button>
                      
                      {/* Pointing finger positioned right below the button */}
                      <PointingFinger />
                    </div>

                    {product.originalPrice && (
                      <div className="absolute top-3 left-3 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                        -{((product.originalPrice - product.price) / product.originalPrice * 100).toFixed(0)}%
                      </div>
                    )}
                  </div>
                </React.Fragment>
              ))}
            </div>
            
            {/* Special offer section */}
            <div className="mt-12 mb-8">
              <div className="bg-gradient-to-r from-blue-50 to-red-50 rounded-2xl p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">عرض خاص لسكان بن عروس، أريانة، منوبة</h3>
                <p className="text-gray-700 text-center mb-4">
                  إيجا بحذانا للمحل وخوذها بسعر خاص كلمنا على الهاتف أو الواتساب نعطيك العنوان،
                </p>
                <p className="text-gray-700 text-center mb-4">
                  العنوان أكتب في قوقل ماب مثل هذه الكتابة بالضبط (hanout 3mara) مرحبا بيك
                </p>
                <div className="flex justify-center mt-4">
                  <a 
                    href="tel:+21653231142" 
                    className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-8 rounded-full transition-colors duration-300 flex items-center gap-2"
                  >
                    <Phone className="w-5 h-5" />
                    اتصل بنا الآن
                  </a>
                </div>
              </div>
              
              <img 
                src="https://i.ibb.co/Y4Q212qr/Screenshot-2025-11-15-at-9-59-58-PM.png" 
                alt="Security Products Showcase" 
                className="w-full rounded-2xl shadow-lg mb-6"
              />
              
              {/* New image added here */}
              <img 
                src="https://i.ibb.co/1GGHqVps/Whats-App-Image-2025-11-15-at-22-18-57.jpg" 
                alt="Security Products Additional" 
                className="w-full rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Purchase Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto pt-10">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative">
            <button 
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>
            
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">أكمل معلوماتك</h3>
            
            {formData.selectedProduct && (
              <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-xl">
                <img 
                  src={formData.selectedProduct.image} 
                  alt={formData.selectedProduct.arabicName}
                  className="w-16 h-16 object-cover rounded-lg"
                />
                <div>
                  <h4 className="font-bold text-gray-800">{formData.selectedProduct.arabicName}</h4>
                  <p className="text-red-600 font-semibold">{formData.selectedProduct.price.toFixed(3)} د.ت</p>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-right flex items-center gap-2">
                  <User className="w-5 h-5 text-red-500" />
                  الاسم الكامل *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`input-modern text-right ${errors.name ? 'form-error' : ''}`}
                  placeholder="أدخل اسمك الكامل"
                  required
                />
                {errors.name && <p className="text-red-500 text-sm mt-1 text-right">{errors.name}</p>}
              </div>
              
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-right flex items-center gap-2">
                  <Phone className="w-5 h-5 text-red-500" />
                  رقم الهاتف *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`input-modern text-right ${errors.phone ? 'form-error' : ''}`}
                  placeholder="20 123 456"
                  required
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1 text-right">{errors.phone}</p>}
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-right flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-red-500" />
                  المدينة *
                </label>
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={`input-modern text-right ${errors.city ? 'form-error' : ''}`}
                  required
                >
                  <option value="">اختر المدينة</option>
                  {tunisianCities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
                {errors.city && <p className="text-red-500 text-sm mt-1 text-right">{errors.city}</p>}
              </div>
              
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-right flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-red-500" />
                  العنوان التفصيلي *
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={`input-modern text-right ${errors.address ? 'form-error' : ''}`}
                  placeholder="الشارع، رقم المنزل، المنطقة"
                  required
                />
                {errors.address && <p className="text-red-500 text-sm mt-1 text-right">{errors.address}</p>}
              </div>

              <div className="space-y-4 mt-8">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Truck className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700 font-medium">توصيل مجاني لكل تونس</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                  <span className="text-gray-700 font-medium">دفع آمن عند الاستلام</span>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary py-4 text-xl"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>جاري الإرسال...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-3">
                    <Send className="w-6 h-6" />
                    <span>تأكيد الطلبية</span>
                  </div>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
      
      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative animate-fade-in success-pulse">
            <button 
              onClick={() => setShowSuccess(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors duration-300"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-500" />
              </div>
              
              <h3 className="text-2xl font-bold text-gray-800 mb-4">تم إرسال الطلبية بنجاح! 🎉</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                شكراً لك! تم استلام طلبيتك وسنتواصل معك في أقرب وقت لتأكيد التفاصيل وترتيب التوصيل.
              </p>
              
              <div className="space-y-3 text-sm text-gray-600 mb-6">
                <p>⏰ سنتصل بك خلال 30 دقيقة</p>
                <p>🚚 التوصيل خلال 24-48 ساعة</p>
                <p>💳 الدفع عند الاستلام</p>
              </div>
              
              <button 
                onClick={() => setShowSuccess(false)}
                className="btn-primary w-full"
              >
                حسناً
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Error Modal */}
      {showError && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative animate-fade-in">
            <button 
              onClick={() => setShowError(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors duration-300"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <X className="w-10 h-10 text-red-500" />
              </div>
              
              <h3 className="text-2xl font-bold text-gray-800 mb-4">حدث خطأ!</h3>
              <p className="text-gray-600 mb-6">
                عذراً، حدث خطأ أثناء إرسال طلبيتك. يرجى المحاولة مرة أخرى أو الاتصال بنا مباشرة.
              </p>
              
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowError(false)}
                  className="btn-secondary flex-1"
                >
                  إغلاق
                </button>
                <a 
                  href="tel:+21653231142"
                  className="btn-primary flex-1"
                >
                  اتصل بنا
                </a>
              </div> 
            </div>
          </div> 
        </div>
      )}
    </section>
  );
};

export default OrderForm;